import type { IntakeSpec, PrintingFunctionalDetails } from '@shared/types';
import type { Part, Joint, Feature } from '@shared/spec';
import { HEAT_SET_DEFAULTS, PRINT_TOLERANCE_FITS } from '@/lib/intakeBuilderDefaults';

function getDetails(intake: IntakeSpec): PrintingFunctionalDetails {
  return intake.details as PrintingFunctionalDetails;
}

export function parts(intake: IntakeSpec): Part[] {
  const d = getDetails(intake);
  const dims = intake.core.size ?? { length: 60, width: 40, height: 4 };
  if (d.functionalRole === 'gear') {
    return [
      {
        id: 'body', name: d.functionalRole, kind: 'custom', materialId: 'mat-primary',
        openscadSnippet: '// gear placeholder', boundingBox: { w: 50, d: 50, h: 10 },
        position: [0, 0, 0], rotation: [0, 0, 0], tags: [d.functionalRole], paramRefs: [],
      },
    ];
  }
  return [
    {
      id: 'body', name: d.functionalRole, kind: 'panel', materialId: 'mat-primary',
      width: dims.length ?? 60, depth: dims.width ?? 40, thickness: dims.height ?? 4,
      cutouts: [],
      position: [0, 0, 0], rotation: [0, 0, 0], tags: [d.functionalRole], paramRefs: [],
    },
  ];
}

export function joints(_intake: IntakeSpec): Joint[] {
  return [];
}

export function features(intake: IntakeSpec): Feature[] {
  if (intake.qualityLevel === 'draft' || intake.qualityLevel === 'standard') return [];

  const d = getDetails(intake);
  const out: Feature[] = [];
  const dims = intake.core.size ?? { length: 60, width: 40, height: 4 };
  const fits = PRINT_TOLERANCE_FITS[d.toleranceClass];

  out.push({
    id: 'edge_chamfer',
    partId: 'body',
    kind: 'edge_treatment',
    treatment: 'chamfer',
    amount: fits.firstLayerChamfer,
    scope: 'bottom',
    position: [0, 0, 0],
    rotation: [0, 0, 0],
    paramRefs: [],
  });

  if (d.insertType !== 'none') {
    const inset = 6;
    const positions: Array<[number, number, number]> = [
      [-((dims.length ?? 60) / 2 - inset), -((dims.width ?? 40) / 2 - inset), 0],
      [(dims.length ?? 60) / 2 - inset, -((dims.width ?? 40) / 2 - inset), 0],
      [-((dims.length ?? 60) / 2 - inset), (dims.width ?? 40) / 2 - inset, 0],
      [(dims.length ?? 60) / 2 - inset, (dims.width ?? 40) / 2 - inset, 0],
    ];
    if (d.insertType === 'heat_set') {
      const ins = HEAT_SET_DEFAULTS.M3;
      for (let i = 0; i < 4; i++) {
        out.push({
          id: `boss_${i + 1}`,
          partId: 'body',
          kind: 'boss',
          diameter: ins.bossOd,
          height: ins.bossHeight,
          axis: 'z',
          holeDiameter: ins.holeId,
          ribCount: 0,
          position: positions[i],
          rotation: [0, 0, 0],
          paramRefs: [],
        });
        out.push({
          id: `insert_${i + 1}`,
          partId: 'body',
          kind: 'insert_hole',
          insertType: 'heat_set',
          size: 'M3',
          outerDiameter: ins.holeId,
          depth: ins.bossHeight - 1,
          axis: 'z',
          position: positions[i],
          rotation: [0, 0, 0],
          paramRefs: [],
        });
      }
    } else {
      for (let i = 0; i < 4; i++) {
        out.push({
          id: `hole_${i + 1}`,
          partId: 'body',
          kind: 'screw_hole',
          size: 'M3',
          length: dims.height ?? 4,
          head: 'flat',
          axis: 'z',
          thread: false,
          teardrop: false,
          position: positions[i],
          rotation: [0, 0, 0],
          paramRefs: [],
        });
      }
    }
  }
  return out;
}
